import { auth, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Converts Firebase authentication errors into user-friendly messages
 * without exposing the underlying authentication mechanism
 */
export const getAuthErrorMessage = (error: any): string => {
  // Handle error objects
  if (error && typeof error === 'object') {
    const errorCode = error.code || '';
    const errorMessage = error.message || '';
    
    // Map Firebase error codes to user-friendly messages
    const errorMap: Record<string, string> = {
      // Email/Password errors
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect email or password.',
      'auth/invalid-credential': 'Incorrect email or password.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
      'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
      
      // Rate limiting
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      
      // Network errors
      'auth/network-request-failed': 'Network error. Please check your connection and try again.',
      'auth/timeout': 'Request timed out. Please try again.',
      
      // Popup errors (for Google Sign-In)
      'auth/popup-closed-by-user': 'Sign-in was cancelled.',
      'auth/cancelled-popup-request': 'Sign-in was cancelled.',
      'auth/popup-blocked': 'Pop-up blocked by browser. Please enable pop-ups and try again.',
      
      // Token/Session errors
      'auth/invalid-api-key': 'Authentication service error. Please contact support.',
      'auth/app-deleted': 'Authentication service error. Please contact support.',
      'auth/expired-action-code': 'This link has expired. Please request a new one.',
      'auth/invalid-action-code': 'This link is invalid. Please request a new one.',
      
      // Missing or invalid inputs
      'auth/missing-email': 'Please enter your email address.',
      'auth/missing-password': 'Please enter your password.',
      'auth/invalid-password': 'Invalid password format.',
      
      // Multi-factor authentication
      'auth/multi-factor-auth-required': 'Additional verification is required.',
      
      // Other errors
      'auth/requires-recent-login': 'Please sign out and sign in again to continue.',
      'auth/credential-already-in-use': 'This credential is already associated with another account.',
    };
    
    // Return mapped error or a generic message
    if (errorCode && errorMap[errorCode]) {
      return errorMap[errorCode];
    }
    
    // If error message contains Firebase-specific text, strip it out
    if (errorMessage) {
      // Remove Firebase prefix and error codes from message
      let cleanMessage = errorMessage
        .replace(/Firebase:\s*/gi, '')
        .replace(/Error\s*\([^)]*\):\s*/gi, '')
        .replace(/\(auth\/[^)]*\)/gi, '')
        .trim();
      
      // If the cleaned message is still meaningful, return it
      if (cleanMessage && cleanMessage.length > 10) {
        return cleanMessage;
      }
    }
  }
  
  // Default fallback message
  return 'An error occurred. Please try again or contact support if the problem persists.';
};

/**
 * Makes an authenticated call to a Firebase callable function
 * Automatically includes the Firebase ID token in the request
 */
export const callAuthenticatedFunction = async (functionName: string, data?: any, timeoutMs: number = 120000) => {
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }

  try {
    // Get the Firebase ID token
    const idToken = await auth.currentUser.getIdToken();
    
    // Use the pre-configured functions instance with the correct region
    // Configure with custom timeout (default 2 minutes, but can be overridden for specific functions)
    const callable = httpsCallable(functions, functionName, {
      timeout: timeoutMs
    });
    
    // Call the function with the token in the context
    // Note: Firebase callable functions automatically include the ID token
    // when called from an authenticated user, but we can also manually set it
    const result = await callable(data);
    
    return result;
  } catch (error: any) {
    console.error(`Error calling function ${functionName}:`, error);
    
    // Handle common Firebase function errors
    if (error.code === 'functions/unauthenticated') {
      throw new Error('Authentication required. Please sign in and try again.');
    } else if (error.code === 'functions/permission-denied') {
      throw new Error('You do not have permission to perform this action.');
    } else if (error.code === 'functions/not-found') {
      throw new Error('The requested function was not found.');
    } else if (error.code === 'functions/internal') {
      throw new Error('An internal server error occurred. Please try again.');
    } else if (error.code === 'functions/unavailable') {
      throw new Error('Service temporarily unavailable. Please try again later.');
    }
    
    throw error;
  }
};

/**
 * Gets the current user's Firebase ID token
 * Useful for making direct HTTP requests to admin endpoints
 */
export const getCurrentUserToken = async (): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }
  
  return await auth.currentUser.getIdToken();
};

/**
 * Makes an authenticated HTTP request to an admin endpoint
 * Includes the Firebase ID token in the Authorization header
 */
export const makeAuthenticatedRequest = async (
  url: string, 
  options: RequestInit = {}
): Promise<Response> => {
  const token = await getCurrentUserToken();
  
  const authenticatedOptions: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  };
  
  return fetch(url, authenticatedOptions);
};
